import React, { useMemo, useState } from "react";
import "../DesignerRenderer.css";

const getShapeType = (dataJson) => dataJson.shape_type || "rectangle";

const getPolygonPointsString = (sides = 6) => {
  const safeSides = Math.max(5, Number(sides) || 6);
  const points = [];

  for (let i = 0; i < safeSides; i += 1) {
    const angle = (Math.PI * 2 * i) / safeSides - Math.PI / 2;
    const x = 50 + 50 * Math.cos(angle);
    const y = 50 + 50 * Math.sin(angle);
    points.push(`${x},${y}`);
  }

  return points.join(" ");
};

const parseCustomPointsToArray = (rawPoints) => {
  if (!rawPoints || typeof rawPoints !== "string") return [];

  return rawPoints
    .trim()
    .split(/\s+/)
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const [x, y] = pair.split(",");
      const nx = Number(x);
      const ny = Number(y);
      if (!Number.isFinite(nx) || !Number.isFinite(ny)) return null;
      return { x: nx, y: ny };
    })
    .filter(Boolean);
};

const stringifyCustomPoints = (points) =>
  points
    .map((p) => `${Math.round(p.x * 100) / 100},${Math.round(p.y * 100) / 100}`)
    .join(" ");

const midpoint = (a, b) => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});

const ShapeRenderer = ({
  object,
  dataJson,
  styleJson,
  commonStyle,
  handleMouseDown,
  renderSelectionHandles,
  selected,
  readOnly,
  updateObjectDataField,
  updateObjectPosition,
  onSelect,
}) => {
  const [activePointIndex, setActivePointIndex] = useState(null);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(null);

  const shapeType = getShapeType(dataJson);
  const renderMode = dataJson.render_mode || "filled";
  const transparentFill = dataJson.transparent_fill ?? false;

  const borderColor = styleJson.borderColor || "#2563eb";
  const borderWidth = Number(styleJson.borderWidth ?? 2);
  const fillColor = transparentFill
    ? "transparent"
    : styleJson.backgroundColor || "#93c5fd";

  const isOutlineOnly = renderMode === "outline";

  const wrapperStyle = {
    position: "absolute",
    ...commonStyle,
    background: "transparent",
    border: "none",
    overflow: "visible",
    outline: selected ? "2px solid #2563eb" : "none",
    outlineOffset: selected ? "2px" : "0",
  };

  const baseShapeStyle = {
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    background: isOutlineOnly ? "transparent" : fillColor,
    border: `${borderWidth}px solid ${borderColor}`,
  };

  const centeredEqualShapeStyle = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
  };

  const centeredEqualInnerStyle = {
    width: `min(${object.width}px, ${object.height}px)`,
    height: `min(${object.width}px, ${object.height}px)`,
    boxSizing: "border-box",
    background: isOutlineOnly ? "transparent" : fillColor,
    border: `${borderWidth}px solid ${borderColor}`,
    flex: "0 0 auto",
  };

  const customPoints = useMemo(
    () => parseCustomPointsToArray(dataJson.custom_points || ""),
    [dataJson.custom_points]
  );

  const customClosed = dataJson.custom_closed ?? true;

  const resetCustomSelection = () => {
    setActivePointIndex(null);
    setActiveSegmentIndex(null);
  };

  const commitCustomPoints = (points) => {
    updateObjectDataField?.(object.id, "custom_points", stringifyCustomPoints(points));
  };

  const startWholeShapeMove = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (readOnly) return;

    onSelect?.(e);

    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const startX = object.x;
    const startY = object.y;

    const handleMove = (moveEvent) => {
      const dx = moveEvent.clientX - startMouseX;
      const dy = moveEvent.clientY - startMouseY;
      updateObjectPosition?.(object.id, startX + dx, startY + dy);
    };

    const handleUp = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  const startPointDrag = (e, pointIndex) => {
    e.stopPropagation();
    e.preventDefault();

    if (readOnly || shapeType !== "custom") return;

    onSelect?.(e);
    setActivePointIndex(pointIndex);
    setActiveSegmentIndex(null);

    const startX = e.clientX;
    const startY = e.clientY;
    const points = parseCustomPointsToArray(dataJson.custom_points || "");
    const current = points[pointIndex];
    if (!current) return;

    const handleMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      const nextX = Math.max(0, Math.min(100, current.x + (dx / object.width) * 100));
      const nextY = Math.max(0, Math.min(100, current.y + (dy / object.height) * 100));

      const nextPoints = [...points];
      nextPoints[pointIndex] = { x: nextX, y: nextY };
      commitCustomPoints(nextPoints);
    };

    const handleUp = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  const handleCustomWrapperMouseDown = (e) => {
    e.stopPropagation();
    onSelect?.(e);

    const target = e.target;

    if (
      target instanceof HTMLElement &&
      target.closest(".dr-shape-floating-action")
    ) {
      return;
    }

    if (
      target instanceof SVGCircleElement ||
      target instanceof SVGLineElement ||
      target instanceof SVGPolygonElement ||
      target instanceof SVGPolylineElement ||
      target instanceof SVGSVGElement
    ) {
      return;
    }

    if (selected) {
      resetCustomSelection();
    }
  };

  const handlePointClick = (e, index) => {
    e.stopPropagation();
    onSelect?.(e);
    setActivePointIndex(index);
    setActiveSegmentIndex(null);
  };

  const handleSegmentClick = (e, index) => {
    e.stopPropagation();
    onSelect?.(e);
    setActiveSegmentIndex(index);
    setActivePointIndex(null);
  };

  const removeActivePoint = () => {
    if (activePointIndex === null) return;

    const points = parseCustomPointsToArray(dataJson.custom_points || "");
    if (points.length <= 2) return;

    const nextPoints = points.filter((_, i) => i !== activePointIndex);
    commitCustomPoints(nextPoints);
    setActivePointIndex(null);
  };

  const insertPointAtSegment = () => {
    if (activeSegmentIndex === null) return;

    const points = parseCustomPointsToArray(dataJson.custom_points || "");
    const isClosed = dataJson.custom_closed ?? true;
    if (points.length < 2) return;

    const a = points[activeSegmentIndex];
    const b =
      activeSegmentIndex === points.length - 1
        ? isClosed
          ? points[0]
          : null
        : points[activeSegmentIndex + 1];

    if (!a || !b) return;

    const mid = midpoint(a, b);
    const insertIndex = activeSegmentIndex + 1;

    const nextPoints = [...points];
    nextPoints.splice(insertIndex, 0, mid);

    commitCustomPoints(nextPoints);
    setActiveSegmentIndex(null);
    setActivePointIndex(insertIndex);
  };

  const renderSvgShape = (points, closed = true) => {
    const fill = isOutlineOnly ? "transparent" : fillColor;

    return (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ display: "block", overflow: "visible" }}
      >
        {closed ? (
          <polygon
            points={points}
            fill={fill}
            stroke={borderColor}
            strokeWidth={borderWidth}
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
          />
        ) : (
          <polyline
            points={points}
            fill="none"
            stroke={borderColor}
            strokeWidth={borderWidth}
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
      </svg>
    );
  };

  let shapeNode = null;

  if (shapeType === "rectangle") {
    shapeNode = (
      <div
        className="dr-shape-inner"
        style={{
          ...baseShapeStyle,
          borderRadius: Number(styleJson.borderRadius || 0),
        }}
      />
    );
  } else if (shapeType === "square") {
    shapeNode = (
      <div style={centeredEqualShapeStyle}>
        <div
          className="dr-shape-inner"
          style={{
            ...centeredEqualInnerStyle,
            borderRadius: Number(styleJson.borderRadius || 0),
          }}
        />
      </div>
    );
  } else if (shapeType === "circle") {
    shapeNode = (
      <div style={centeredEqualShapeStyle}>
        <div
          className="dr-shape-inner"
          style={{
            ...centeredEqualInnerStyle,
            borderRadius: "50%",
          }}
        />
      </div>
    );
  } else if (shapeType === "ellipse") {
    shapeNode = (
      <div
        className="dr-shape-inner"
        style={{
          ...baseShapeStyle,
          borderRadius: "50%",
        }}
      />
    );
  } else if (shapeType === "triangle") {
    shapeNode = renderSvgShape("50,0 0,100 100,100", true);
  } else if (shapeType === "polygon") {
    shapeNode = renderSvgShape(
      getPolygonPointsString(dataJson.polygon_sides || 6),
      true
    );
  } else if (shapeType === "line") {
    const lineThickness = Math.max(
      1,
      Number(styleJson.lineThickness || borderWidth || 2)
    );
    const lineColor = styleJson.lineColor || borderColor;

    shapeNode = (
      <div
        className="dr-shape-line"
        style={{
          width: "100%",
          height: `${lineThickness}px`,
          background: lineColor,
          marginTop: `calc(50% - ${lineThickness / 2}px)`,
        }}
      />
    );
  } else if (shapeType === "custom") {
    const pointsString = customPoints.map((p) => `${p.x},${p.y}`).join(" ");
    const fill = isOutlineOnly ? "transparent" : fillColor;

    const segmentDefs = [];
    if (customPoints.length >= 2) {
      for (let i = 0; i < customPoints.length - 1; i += 1) {
        segmentDefs.push({
          index: i,
          a: customPoints[i],
          b: customPoints[i + 1],
        });
      }
      if (customClosed && customPoints.length > 2) {
        segmentDefs.push({
          index: customPoints.length - 1,
          a: customPoints[customPoints.length - 1],
          b: customPoints[0],
        });
      }
    }

    shapeNode = (
      <div className="dr-shape-custom-wrapper">
        <svg
          className="dr-shape-svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          width="100%"
          height="100%"
          onMouseDown={handleCustomWrapperMouseDown}
        >
          {customClosed ? (
            <polygon
              points={pointsString}
              fill={fill}
              stroke={borderColor}
              strokeWidth={borderWidth}
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
            />
          ) : (
            <polyline
              points={pointsString}
              fill="none"
              stroke={borderColor}
              strokeWidth={borderWidth}
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {!readOnly &&
            selected &&
            segmentDefs.map((seg) => (
              <line
                key={`seg-${seg.index}`}
                x1={seg.a.x}
                y1={seg.a.y}
                x2={seg.b.x}
                y2={seg.b.y}
                className={`dr-shape-segment-hit ${
                  activeSegmentIndex === seg.index ? "dr-shape-segment-hit--active" : ""
                }`}
                onClick={(e) => handleSegmentClick(e, seg.index)}
              />
            ))}

          {!readOnly &&
            selected &&
            customPoints.map((p, index) => (
              <circle
                key={index}
                cx={p.x}
                cy={p.y}
                r="2.5"
                className={`dr-shape-point ${
                  activePointIndex === index ? "dr-shape-point--active" : ""
                }`}
                onMouseDown={(e) => startPointDrag(e, index)}
                onClick={(e) => handlePointClick(e, index)}
              />
            ))}
        </svg>

        {!readOnly && selected && (
          <button
            type="button"
            className="dr-shape-floating-action dr-shape-floating-action--move"
            onMouseDown={startWholeShapeMove}
          >
            Move
          </button>
        )}

        {!readOnly && selected && activePointIndex !== null && (
          <button
            type="button"
            className="dr-shape-floating-action dr-shape-floating-action--danger"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              removeActivePoint();
            }}
          >
            Remove Point
          </button>
        )}

        {!readOnly && selected && activeSegmentIndex !== null && (
          <button
            type="button"
            className="dr-shape-floating-action dr-shape-floating-action--add"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              insertPointAtSegment();
            }}
          >
            Add Point
          </button>
        )}
      </div>
    );
  } else {
    shapeNode = (
      <div
        className="dr-shape-inner"
        style={{
          ...baseShapeStyle,
          borderRadius: Number(styleJson.borderRadius || 0),
        }}
      />
    );
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseDown={
        shapeType === "custom"
          ? handleCustomWrapperMouseDown
          : (e) => {
              e.stopPropagation();
              handleMouseDown?.(e);
            }
      }
      className="dr-object dr-shape"
      style={wrapperStyle}
    >
      {shapeNode}
      {renderSelectionHandles()}
    </div>
  );
};

export default ShapeRenderer;